import { PROJECT_CONFIG } from '../config/projectConfig';
import { Message, ChatState } from '../models/types';
import apiService from './apiService';
import matterportService from './matterportService';

class ChatService {
  constructor() {
    this.state = new ChatState();
    this.eventHandlers = new Map();
    this.messageIdCounter = 0; // Add counter for unique IDs
    this.isInitialized = false; // Add initialization flag
  }

  // Generate unique message ID
  generateUniqueMessageId() {
    this.messageIdCounter += 1;
    return `${Date.now()}_${this.messageIdCounter}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Initialize chat with initial message
  initialize() {
    // Prevent multiple initializations
    if (this.isInitialized) {
      return;
    }
    
    const initialMessage = new Message(
      this.generateUniqueMessageId(),
      `Hello! I'm here to help you learn about the ${this.getProjectName()}. Ask me anything about the facility, its operations, or technical specifications!`,
      "bot",
      new Date(),
      "OpenAI"
    );

    this.state.messages = [initialMessage];
    this.state.isOpen = false;
    this.state.isMinimized = false;
    this.state.isTyping = false;
    this.state.conversationId = null;
    
    this.isInitialized = true; // Mark as initialized
  }

  // Send a message
  async sendMessage(messageText) {
    if (!messageText.trim() || this.state.isTyping) return;

    const userMessage = new Message(
      this.generateUniqueMessageId(),
      messageText.trim(),
      "user",
      new Date()
    );

    this.addMessage(userMessage);
    this.setState({ isTyping: true });

    try {
      // Get current spatial context
      const spatialInfo = matterportService.getCurrentSpatialInfo();
      
      // Prepare conversation history
      const conversationHistory = this.state.messages.map(msg => ({
        text: msg.text,
        isBot: msg.sender === "bot"
      }));

      // Send to backend
      const response = await apiService.sendChatMessage(
        messageText.trim(),
        this.state.conversationId,
        conversationHistory,
        spatialInfo.spatial_context
      );

      console.log('📤 Full backend response:', response);
      console.log('📤 Response keys:', Object.keys(response));
      console.log('📤 Function call:', response.function_call);
      console.log('📤 Response text:', response.response);

      // Handle function calls first
      if (response.function_call) {
        console.log('🔧 Function call detected:', response.function_call);
        await this.handleFunctionCall(response.function_call);
        
        // Add function call info message if needed
        const functionMessage = this.createFunctionCallInfo(response.function_call);
        if (functionMessage.text) {
          this.addMessage(functionMessage);
        }
      }

      // Add bot response only if it's not empty or just JSON
      if (response.response && response.response.trim() && !response.response.startsWith('{')) {
        const botMessage = new Message(
          this.generateUniqueMessageId(),
          response.response,
          "bot",
          new Date(),
          "OpenAI",
          response.function_call
        );
        this.addMessage(botMessage);
      } else if (response.response && response.response.startsWith('{')) {
        console.log('🚫 Blocked JSON response from appearing in chat:', response.response);
      }

      // Update conversation ID
      if (!this.state.conversationId) {
        this.setState({ conversationId: response.conversation_id });
      }

      // Keep history manageable
      if (this.state.messages.length > 20) {
        this.state.messages = this.state.messages.slice(-20);
      }

    } catch (error) {
      console.error('❌ Chat error:', error);
      
      let errorMessage = "Sorry, I'm having trouble connecting to the server. Please try again later.";
      
      if (error.name === "TypeError" && error.message.includes("Failed to fetch")) {
        errorMessage = "Network error: Unable to reach the server. This might be a CORS issue or the server might be down.";
      } else if (error.message.includes("CORS")) {
        errorMessage = "CORS error: The server is not allowing requests from this domain.";
      } else if (error.message.includes("insufficient_quota")) {
        errorMessage = "⚠️ OpenAI API quota exceeded. Please check your billing details or try again later.";
      }

      const errorBotMessage = new Message(
        this.generateUniqueMessageId(),
        errorMessage,
        "bot",
        new Date(),
        "OpenAI"
      );
      
      this.addMessage(errorBotMessage);
    } finally {
      this.setState({ isTyping: false });
    }
  }

  // Handle function calls from AI
  async handleFunctionCall(functionCall) {
    try {
      console.log('🔧 Handling function call:', functionCall);
      
      const { function: functionName, parameters, result } = functionCall;
      
      // Log the function call details
      console.log('🔧 Function:', functionName);
      console.log('🔧 Parameters:', parameters);
      console.log('🔧 Result:', result);
      
      if (!result.success) {
        console.error('❌ Function call failed:', result.message);
        return false;
      }
      
      switch (functionName) {
        case 'navigate_to_tag':
          console.log('🎯 Executing navigation to:', parameters.label);
          if (result.result && result.result.tag) {
            console.log('📍 Found tag:', result.result.tag);
            const success = await matterportService.navigateToTag(parameters.label);
            console.log('🎯 Navigation result:', success);
            return success;
          } else {
            console.error('❌ No matching tag found for:', parameters.label);
            console.log('📍 Available tags:', result.result?.available_tags || []);
            return false;
          }

        case 'get_spatial_info':
          console.log('📍 Getting spatial info:', parameters.info_type);
          // Don't create message here - let createFunctionCallInfo handle it
          return true;
          
        default:
          console.warn('⚠️ Unknown function:', functionName);
          return false;
      }
    } catch (error) {
      console.error('❌ Error handling function call:', error);
      return false;
    }
  }

  // Create function call info message
  createFunctionCallInfo(functionCall) {
    let infoText = '';
    
    if (functionCall.function === 'navigate_to_tag' && functionCall.result?.result?.tag) {
      // Navigation function - don't show navigation status messages
      infoText = '';
    } else if (functionCall.function === 'get_spatial_info') {
      // Spatial info function
      if (functionCall.result?.info?.current_tag) {
        const tag = functionCall.result.info.current_tag;
        infoText = `📍 You are looking at the ${tag.label}.\n\n${tag.description || ''}`;
      } else {
        infoText = `📍 ${functionCall.result?.info || 'No specific location in view.'}`;
      }
    }

    return new Message(
      this.generateUniqueMessageId(),
      infoText,
      "bot",
      new Date(),
      "OpenAI",
      functionCall
    );
  }

  // Add message to chat
  addMessage(message) {
    // Message ID should already be unique from generation, but ensure it
    if (!message.id || message.id === 0) {
      message.id = this.generateUniqueMessageId();
    }
    this.state.messages.push(message);
    this.notifyEventHandlers('messageAdded', message);
  }

  // Get all messages
  getMessages() {
    return this.state.messages;
  }

  // Get current state
  getState() {
    return { ...this.state };
  }

  // Set state
  setState(newState) {
    this.state = { ...this.state, ...newState };
    this.notifyEventHandlers('stateChanged', this.state);
  }

  // Toggle chat visibility
  toggleChat() {
    const newIsOpen = !this.state.isOpen;
    this.setState({ 
      isOpen: newIsOpen,
      isMinimized: newIsOpen ? false : this.state.isMinimized
    });
  }

  // Toggle minimized state
  toggleMinimized() {
    this.setState({ isMinimized: !this.state.isMinimized });
  }

  // Close chat
  closeChat() {
    this.setState({ isOpen: false });
    this.isInitialized = false; // Reset initialization flag
  }

  // Get project name
  getProjectName() {
    return PROJECT_CONFIG.project.name || "N/A";
  }

  // Event handling
  addEventListener(event, handler) {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event).push(handler);
  }

  removeEventListener(event, handler) {
    if (this.eventHandlers.has(event)) {
      const handlers = this.eventHandlers.get(event);
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  notifyEventHandlers(event, data) {
    if (this.eventHandlers.has(event)) {
      this.eventHandlers.get(event).forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in event handler for ${event}:`, error);
        }
      });
    }
  }

  // Clear chat history
  clearHistory() {
    this.state.messages = [];
    this.state.conversationId = null;
    this.isInitialized = false; // Reset initialization flag
    this.notifyEventHandlers('historyCleared');
  }

  // Cleanup
  cleanup() {
    this.eventHandlers.clear();
    this.state = new ChatState();
  }
}

// Create singleton instance
const chatService = new ChatService();
export default chatService; 